import './App.css';
import { useContext, useEffect, Suspense, lazy } from 'react';
import classNames from 'classnames';
import { NavbarStyles as styles } from './css/styles';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router';
import { AuthContext } from './context/AuthContext';
import Navbar from './components/Navbar';
import { setupAxiosInterceptors } from './services/api';
import { configurePdfWorker } from '../src/utils/configPdfWorker';
import { LoadingSpinner } from './components/Spinner';
import CookieConsentBanner from './components/CookieConsentBanner';
import { REGION } from './utils/region';
import PrivacyRU from './pages/Privacy';
import PrivacyEU from './pages/PrivacyEU';
import PrivacyUS from './pages/PrivacyUS';
import TermsRU from './pages/UserAgreement';
import TermsEU from './pages/UserAgreementEU';
import TermsUS from './pages/UserAgreementUS';
import { soundEngine } from './utils/soundEngine';
import { trackPageView } from './utils/yandexMetrica';
import { hasAnalyticsConsent } from './utils/cookieConsent';
import { ADMIN_USER_ID } from './pages/Admin';
import SelfQuizPlatform from './pages/SelfQuizPlatform';

const privacyPages = { ru: PrivacyRU, eu: PrivacyEU, us: PrivacyUS };
const termsPages  = { ru: TermsRU,   eu: TermsEU,   us: TermsUS   };
const PrivacyPage = privacyPages[REGION];
const TermsPage   = termsPages[REGION];
const Home = lazy(() => import('./pages/Home'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Courses = lazy(() => import('./pages/Courses'));
const Settings = lazy(() => import('./pages/Settings'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const LessonBuilder = lazy(() => import('./pages/LessonBuilder'));
const Units = lazy(() => import('./pages/Units'));
const Lessons = lazy(() => import('./pages/Lessons'));
const Folders = lazy(() => import('./pages/Folders'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));
const Profile = lazy(() => import('./pages/Profile'));
const MyCalendar = lazy(() => import('./pages/Calendar'));
const Students = lazy(() => import('./pages/Students'));
const Student = lazy(() => import('./pages/Student'));
const HomeworkCheck = lazy(() => import('./pages/HomeworkCheck'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const StudentDeleted = lazy(() => import('./pages/StudentDeleted'));
const TestCompleted = lazy(() => import('./pages/TestCompleted'));
const Rewards = lazy(() => import('./pages/Rewards'));
const Group = lazy(() => import('./pages/Group'));
const StudentOverview = lazy(() => import('./pages/StudentOverview'));
const ConfirmDelete = lazy(() => import('./pages/ConfirmDelete'));
const InviteCancel = lazy(() => import('./pages/InviteCancel'));
const Help = lazy(() => import('./pages/help'));
const Admin = lazy(() => import('./pages/Admin'));


const AppContent: React.FC = () => {
    const { currentUser, refreshToken, logout } = useContext(AuthContext);
    configurePdfWorker();
    const location = useLocation();
    const navigate = useNavigate();
    const hideNavbarPaths = ['/lesson-builder', '/test-completed', '/lesson/'];
    const showNavbar = !hideNavbarPaths.some(path => location.pathname.includes(path));
    const isAuthPage = ['/register', '/login'].includes(location.pathname);

    useEffect(() => {
        setupAxiosInterceptors(refreshToken, logout, () => navigate('/login'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasAnalyticsConsent()) trackPageView(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        const isHomeRoute =
          location.pathname === "/" || location.pathname === "/home";
    
        if (!currentUser && isHomeRoute) {
          document.body.classList.add("home-bg");
        } else {
          document.body.classList.remove("home-bg");
        }
      }, [currentUser, location.pathname]);

    const studentRoutes = currentUser?.mode === 'student' ? (
        <Route path="/rewards" element={<Rewards />} />
    ) : null;


    //SOUND INIT
    useEffect(() => {
        const unlock = async () => {
            await soundEngine.init()
        }

        window.addEventListener('pointerdown', unlock, { once: true })

        return () => {
            window.removeEventListener('pointerdown', unlock)
        }
    }, [])

    return (
        <>
            {showNavbar && 
                <Navbar className={classNames({[styles.navbarRegister]: isAuthPage, [styles.navbar]: !isAuthPage})}/>
            }
            <div className={
                (location.pathname.includes('/lesson-builder') || location.pathname.includes('/lesson/')) ? "lesson-builder" :
                location.pathname.includes('/test-viewer') ? "test-viewer" : "App"}
            >
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/verify/:code" element={<AuthPage />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/404" element={<ErrorPage />} />
                        <Route path="*" element={<ErrorPage />} />
                        <Route path="/register" element={<AuthPage />} />
                        <Route path="/cancel-invitation/:studentCourseId/:studentId/:teacherId" element={<InviteCancel />} />
                        <Route path="/register/:urlCode/:studentId" element={<AuthPage />} />
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/privacy/ru" element={<PrivacyRU />} />
                        <Route path="/privacy/en" element={<PrivacyEU />} />
                        <Route path="/privacy/us" element={<PrivacyUS />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/contacts" element={<Contacts />} />
                        <Route path="/home" element={<Home />} />
                        {studentRoutes}
                        {currentUser ? (
                            <>
                                <Route path="/" element={<Courses />} />
                                <Route path="/courses" element={<Courses />} />
                                <Route path="/folders/:folderId/*" element={<Folders />} />
                                <Route path="/courses/:courseId/units" element={<Units />} />
                                <Route path="/courses/:courseId/units/:unitId/lessons" element={<Lessons />} />
                                <Route path="/settings" element={<Settings />} />
                                <Route path="/profile/:teacherId" element={<Profile />} />
                                <Route path="/calendar" element={<MyCalendar />} />
                                <Route path="/homework-check/:homeworkId/:submitNumber" element={<HomeworkCheck />} />
                                <Route path="/self-quiz" element={<SelfQuizPlatform />} />
                                <Route path="/students" element={<Students />} />
                                <Route path="/student/:studentId" element={<StudentOverview />} />
                                <Route path="/lesson-builder/:courseId/:unitId/:lessonId" element={<LessonBuilder />} />
                                <Route path="/lesson/:courseId/:unitId/:lessonId/:lessonType/:entityId" element={<LessonBuilder />} />
                                <Route path="/student-deleted" element={<StudentDeleted />} />
                                <Route path="/test-completed" element={<TestCompleted />} />
                                <Route path="/group/:groupId" element={<Group />} />
                                <Route path="/studentcourse/:studentId/:courseId" element ={<Student/>} />
                                <Route path="/confirm-delete" element={<ConfirmDelete />} />
                                <Route path="/help" element={<Help />} />
                                {currentUser?.userId === ADMIN_USER_ID && (
                                    <Route path="/admin" element={<Admin />} />
                                )}
                            </>
                        ) : (
                            <Route path="/" element={<Home />} />
                        )}
                    </Routes>
                </Suspense>
                
            </div>
        </>
        );
    };
    
    const PageContainer: React.FC = () => {
        const location = useLocation();
        const isLessonBuilder = location.pathname.includes('/lesson-builder') || location.pathname.includes('/lesson/');
        return (
            <div className={isLessonBuilder ? "page-container lesson-builder-bg" : "page-container"}>
                <div className="main-content">
                    <AppContent />
                </div>
            </div>
        );
    };

    const App: React.FC = () => {
        return (
            <Router>
                <CookieConsentBanner />
                <PageContainer />
            </Router>
        );
    }

    export default App;




